import { Button, ButtonMode, ButtonSize, Checkbox, Icon, ICONS, Popup, Tooltip } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { sortBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobalModalContext } from '../../../components/GlobalModal';
import { MODAL_TYPES } from '../../../components/GlobalModal/GlobalModal';
import WatchlistPopupFooter from '../../../components/AddToWatchlist/WatchlistPopupFooter';
import useFetchUserProfile from '../../../hooks/profile/useFetchUserProfile';
import { useToast } from '../../../hooks/toast/useToast';
import { useFetchWatchLists, useUpdateWatchList } from '../../../hooks/watchLists';
import { WatchList } from '../../../models/WatchList';
import {
  getIsUserLimitExceeded,
  getIsWatchlistLimitExceeded,
  USER_LIMIT_EXCEEDED_MESSAGE_COMPANY_PAGE,
  WATCHLIST_LIMIT_EXCEEDED_MESSAGE_COMPANY_PAGE,
} from '../../MyContent/WatchlistsTab/Watchlist/Watchlist.logic';
import { trackCompanyWatchlistChanges } from '../../../utils/trackUsageUtils';
import styles from '../../WachlistPopup.module.scss';

const cx = classNames.bind(styles);

interface CompanyAddToWatchlistActionProps {
  issuerTag: string;
}

const CompanyAddToWatchlistAction = ({ issuerTag }: CompanyAddToWatchlistActionProps): React.ReactElement => {
  const { data: profile } = useFetchUserProfile();
  const { data: watchLists, isLoading: isLoadingWatchlists } = useFetchWatchLists({
    select: (data) => data.filter((item) => item.ownerId === profile?.userId),
  });
  const { mutate: updateWatchlist, isLoading: isUpdatingWatchlist } = useUpdateWatchList();
  const [isOpen, setIsOpen] = useState(false);
  const [isCompanyInAnyWatchlist, setIsCompanyInAnyWatchlist] = useState(false);
  const { showToast } = useToast();
  const { showModal } = useGlobalModalContext();

  const watchlistsWithCurrentCompany = useMemo(
    () =>
      new Set(watchLists?.filter((watchList) => watchList.tags.includes(issuerTag)).map((watchList) => watchList.id)),
    [watchLists, issuerTag]
  );
  const isUserLimitExceeded = getIsUserLimitExceeded(watchLists);
  const isPopupTriggerDisabled = isUserLimitExceeded && !isCompanyInAnyWatchlist;
  const sortedWatchlists = useMemo(() => sortBy(watchLists, (list) => list.name.toLowerCase()), [watchLists]);
  const hasWatchlists = watchLists && watchLists.length > 0;

  useEffect(() => {
    setIsCompanyInAnyWatchlist(watchlistsWithCurrentCompany.size > 0);
    if (isPopupTriggerDisabled) {
      setIsOpen(false);
    }
  }, [watchlistsWithCurrentCompany]);

  const handleCheckboxToggle = (watchList: WatchList, isChecked: boolean): void => {
    if (isChecked) {
      const removeCompanyPayload = {
        id: watchList.id,
        watchList: {
          tags: watchList.tags.filter((tag) => !tag.includes(issuerTag)),
        },
      };
      updateWatchlist(removeCompanyPayload, {
        onSuccess: () => {
          trackCompanyWatchlistChanges(watchList.id, issuerTag, isChecked);
          showToast(
            <span>
              The company has been removed from the Watchlist <strong>{watchList.name}</strong>
            </span>
          );
        },
      });
    } else {
      const addCompanyPayload = {
        id: watchList.id,
        watchList: {
          name: watchList.name,
          myCS: watchList.myCS,
          tags: [...watchList.tags, issuerTag],
          mode: watchList.mode,
        },
      };
      updateWatchlist(addCompanyPayload, {
        onSuccess: () => {
          trackCompanyWatchlistChanges(watchList.id, issuerTag, isChecked);
          showToast(
            <span>
              The company has been added to the Watchlist <strong>{watchList.name}</strong>
            </span>
          );
        },
      });
    }
  };

  const onCreateNewWatchlistClick = useCallback(() => {
    showModal(MODAL_TYPES.CREATE_WATCHLIST, {
      watchlists: watchLists,
      issuerTags: [issuerTag],
      setIsOpen,
      setIsCompanyInAnyWatchlist,
    });
  }, [showModal, watchLists]);

  const popupTriggerLabel = isCompanyInAnyWatchlist ? 'Watching' : 'Add to Watchlist';

  const popupTrigger = (
    <div>
      <Tooltip content={USER_LIMIT_EXCEEDED_MESSAGE_COMPANY_PAGE} disabled={!isPopupTriggerDisabled}>
        <div>
          <Button
            loading={isLoadingWatchlists || isUpdatingWatchlist}
            disabled={isLoadingWatchlists || isUpdatingWatchlist || isPopupTriggerDisabled}
            mode={ButtonMode.PRIMARY}
            size={ButtonSize.MEDIUM}
            ariaLabel={popupTriggerLabel}
            className="no-print to-translate"
          >
            {!isCompanyInAnyWatchlist && <Icon width={12} height={12} name={ICONS.Plus} />}
            {popupTriggerLabel}
            {isCompanyInAnyWatchlist && (
              <span className={cx('caret-icon', { 'is-open': isOpen })}>
                <Icon width={12} height={12} name={ICONS.CaretDown} />
              </span>
            )}
          </Button>
        </div>
      </Tooltip>
    </div>
  );

  const renderCheckbox = (watchList: WatchList) => {
    const isChecked = watchlistsWithCurrentCompany.has(watchList.id);
    const isWatchlistLimitExceeded = getIsWatchlistLimitExceeded(watchList);
    const isCheckboxDisabled = !isChecked && (isUserLimitExceeded || isWatchlistLimitExceeded);
    const tooltipMessage = isUserLimitExceeded
      ? USER_LIMIT_EXCEEDED_MESSAGE_COMPANY_PAGE
      : WATCHLIST_LIMIT_EXCEEDED_MESSAGE_COMPANY_PAGE;

    return (
      <Tooltip key={watchList.id} content={tooltipMessage} disabled={!isCheckboxDisabled}>
        <Checkbox
          checked={isChecked}
          className={cx('checkbox', { 'active-tooltip': isCheckboxDisabled })}
          dataTestId="add-remove-issuer-checkbox"
          disabled={isCheckboxDisabled}
          label={watchList.name}
          onChange={() => handleCheckboxToggle(watchList, isChecked)}
        />
      </Tooltip>
    );
  };

  return (
    <Popup
      position="bottom right"
      className={cx('dropdown')}
      trigger={popupTrigger}
      on="click"
      open={isOpen}
      onOpen={() => !isPopupTriggerDisabled && setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      data-testid="company-add-to-watchlist-popup"
    >
      {hasWatchlists && (
        <div className={cx('popup-content')}>{sortedWatchlists?.map((watchList) => renderCheckbox(watchList))}</div>
      )}
      <WatchlistPopupFooter
        hasWatchlists={hasWatchlists}
        isUserLimitExceeded={isUserLimitExceeded}
        onCreateNewWatchlistClick={onCreateNewWatchlistClick}
      />
    </Popup>
  );
};

export default CompanyAddToWatchlistAction;
